import { useRef, useMemo } from 'react';

import classNames from 'classnames';
import { ReactTable, Tooltip } from '@crazyegginc/hatch';

import { usePermissions, useAuthContext, useQueryParams } from '/src/hooks';
import { formatDate } from '/src/utils/date';
import { abTestTypes, AB_TEST_STATUSES } from '/src/features/ab-testing/constants';
import { abTestDuration } from '/src/features/ab-testing/utils';
import { FEATURES } from '/src/features/_global/constants';

import { ReactComponent as StatusRunningIcon } from '@crazyegginc/hatch/dist/images/icon-play-filled.svg';
import { ReactComponent as StatusPausedIcon } from '@crazyegginc/hatch/dist/images/icon-pause-filled.svg';
import { ReactComponent as StatusStoppedIcon } from '@crazyegginc/hatch/dist/images/icon-stop-outline.svg';
import { ReactComponent as StatusErrorIcon } from '@crazyegginc/hatch/dist/images/icon-cross-circle-filled.svg';
import { AppLink } from '/src/features/_global/components/AppLink';

export function GoalAbTestsTable({ abTests = [], loading = false }) {
  const permissions = usePermissions();
  const canCreateAbTests = permissions.can('create', FEATURES.AB_TESTING).allowed;
  const { isSharing } = useAuthContext();
  const { get: paramGet } = useQueryParams();
  const date_range = paramGet('date_range');
  const tableRef = useRef(null);
  const columns = useMemo(
    () => [
      {
        header: () => {
          return <div className="pl-2.5">Name</div>;
        },
        accessorKey: 'name',
        size: 35,
        meta: {
          justify: 'left',
          align: 'center',
          padding: '0px 0px 0px 15px',
        },
        cell: ({ row }) => {
          if (
            !isSharing &&
            row.original.status !== AB_TEST_STATUSES.DELETED &&
            (row.original.status !== AB_TEST_STATUSES.DRAFT || canCreateAbTests)
          ) {
            return (
              <AppLink
                to={{
                  pathname:
                    row.original.status === AB_TEST_STATUSES.DRAFT
                      ? `/ab-tests/${row.original.id}/edit`
                      : `/ab-tests/${row.original.id}`,
                  search: `?back=${window.location.pathname}&date_range=${date_range}`,
                }}
                className="text-link mr-4 truncate"
                title={row.original.name}
              >
                {row.original.name}
              </AppLink>
            );
          }
          return (
            <span className="text-link mr-4 truncate !text-lynch-500 hover:!no-underline">{row.original.name}</span>
          );
        },
      },
      {
        header: 'URL',
        accessorKey: 'pageUrl',
        size: 35,
        meta: {
          justify: 'left',
          align: 'center',
        },
        cell: ({ row }) => {
          const { pageUrl } = row.original;

          return (
            <div className="flex w-full flex-wrap">
              <div className="line-clamp-2 w-full break-all leading-none">{pageUrl}</div>
            </div>
          );
        },
      },
      {
        header: () => <span className="text-right w-full px-5">Variants</span>,
        accessorKey: 'variants',
        size: 14,
        meta: {
          justify: 'center',
          align: 'right',
        },
        cell: ({ row }) => {
          const { status, variants, type, variantsCount, pageUrl } = row.original;

          return (
            <div
              className={classNames('w-full flex justify-end px-5', {
                'text-lynch-500': status !== AB_TEST_STATUSES.RUNNING,
              })}
              data-testid={`ab-test-variants-cell-${row.original.id}`}
            >
              <div className="text-right">
                {variants.length > 1 && type === abTestTypes.SPLIT ? (
                  <Tooltip
                    placement="bottom"
                    tooltipContent={
                      <div className="max-w-[225px]" data-testid={`ab-test-variants-tooltip-${row.original.id}`}>
                        <ul className="ml-5 list-outside list-disc space-y-1 text-left text-xs marker:text-cadet-blue-500">
                          {variants.map((variant) => (
                            <li
                              key={variant.id}
                              className="mb-1.25 w-full break-all"
                              data-testid={`ab-test-variants-tooltip-row-${row.original.id}-${variant.id}`}
                            >
                              <div
                                className="font-bold leading-tight"
                                data-testid={`ab-test-variants-tooltip-url-${row.original.id}-${variant.id}`}
                              >
                                {variant.redirectUrl ?? pageUrl}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    }
                  >
                    <span
                      className="text-xs text-lynch-500 underline decoration-cadet-blue-500 decoration-dashed underline-offset-4"
                      data-testid={`ab-test-variants-trigger-${row.original.id}`}
                    >
                      {variantsCount || 0}
                    </span>
                  </Tooltip>
                ) : (
                  variantsCount || 0
                )}
              </div>
            </div>
          );
        },
      },
      {
        header: () => <div className="px-5">Status</div>,
        accessorKey: 'status',
        size: 14,
        meta: {
          justify: 'left',
          align: 'center',
        },
        cell: ({ row }) => <div className="px-5">{RenderStatusCell(row.original.status)}</div>,
      },
      {
        header: () => <span className="px-5">Duration</span>,
        size: 14,
        accessorKey: 'duration',
        meta: {
          justify: 'left',
          align: 'center',
        },
        cell: ({ row }) => {
          const duration = abTestDuration(row.original);
          return (
            <Tooltip
              tooltipContent={
                <div className="flex flex-col">
                  <span className="font-normal">
                    Created <span className="font-semibold">{formatDate(row.original.createdAt)}</span>
                  </span>
                  {row.original.stoppedAt ? (
                    <span className="font-normal">
                      Ended <span className="font-semibold">{formatDate(row.original.stoppedAt)}</span>
                    </span>
                  ) : null}
                </div>
              }
            >
              <span
                className={classNames('px-5 underline decoration-cadet-blue-500 decoration-dashed underline-offset-4', {
                  'text-lynch-500': row.original.status !== AB_TEST_STATUSES.RUNNING,
                })}
              >
                {duration.value}
              </span>
            </Tooltip>
          );
        },
      },
      {
        header: () => <div className="w-full text-right ">Visits</div>,
        accessorKey: 'totalVisitors',
        size: 12,
        meta: {
          justify: 'center',
          align: 'right',
        },
        cell: ({ row }) => (
          <div
            className={classNames('text-right w-full', {
              'text-lynch-500': row.original.status !== AB_TEST_STATUSES.RUNNING,
            })}
          >
            {row?.original?.totalVisitors.toLocaleString() || 0}
          </div>
        ),
      },
      {
        header: () => <span className="w-full text-right">Conversions</span>,
        size: 15,
        accessorKey: 'conversions',
        meta: {
          justify: 'center',
          align: 'right',
        },
        cell: ({ row }) => {
          const totalConversions = row?.original?.totalConversions.toLocaleString() || 0;
          return (
            <div className="w-full flex justify-end">
              <div className="text-right">
                <Tooltip
                  show={row.original.variants.length > 1}
                  placement="left"
                  tooltipContent={
                    <div className="flex flex-col" data-testid={`ab-test-conversions-tooltip-${row.original.id}`}>
                      {row.original.variants.map((variant) => {
                        return (
                          <div
                            key={`${row.original.id}_${variant.id}_variant`}
                            className="flex justify-between"
                            data-testid={`ab-test-conversions-tooltip-row-${row.original.id}-${variant.id}`}
                          >
                            <div
                              className="flex flex-1 justify-start"
                              data-testid={`ab-test-conversions-tooltip-variant-${row.original.id}-${variant.id}`}
                            >
                              <span className="text-xs">{variant.variantName}</span>
                            </div>
                            <span
                              className="ml-8 text-xs font-semibold"
                              data-testid={`ab-test-conversions-tooltip-count-${row.original.id}-${variant.id}`}
                            >
                              {variant?.results?.conversions.toLocaleString() || 0}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  }
                >
                  <span
                    className={classNames(
                      'ml-1.5 underline decoration-cadet-blue-500 decoration-dashed underline-offset-4',
                      {
                        'text-lynch-500': row.original.status !== AB_TEST_STATUSES.RUNNING,
                      },
                    )}
                    data-testid={`ab-test-conversions-trigger-${row.original.id}`}
                  >
                    {totalConversions}
                  </span>
                </Tooltip>
              </div>
            </div>
          );
        },
      },
    ],
    [isSharing, canCreateAbTests, date_range],
  );

  return (
    <div id="ab-tests" className={classNames()}>
      <ReactTable
        ref={{ tableRef }}
        useVirtualization={false}
        fetching={loading}
        rowPadding={true}
        rowHeight={64}
        headerHeight={70}
        columns={columns}
        data={abTests}
        enableSorting={false}
        rowCustomClassname={(row) => {
          return classNames({
            '!text-lynch-500': row && row.original.status === AB_TEST_STATUSES.DELETED,
          });
        }}
      />
    </div>
  );
}

function Dot() {
  return <span className="text-caption block h-2 w-2 rounded-full bg-current">&nbsp;</span>;
}

function RenderStatusCell(status) {
  let Icon, text;
  let className = '';

  switch (status) {
    case AB_TEST_STATUSES.RUNNING:
      Icon = StatusRunningIcon;
      text = 'Running';
      className = 'text-lima-500';
      break;
    case AB_TEST_STATUSES.PAUSED:
      Icon = StatusPausedIcon;
      text = 'Paused';
      className = 'text-lynch-500';
      break;
    case AB_TEST_STATUSES.STOPPED:
      Icon = StatusStoppedIcon;
      text = 'Ended';
      className = 'text-california-500';
      break;
    case AB_TEST_STATUSES.DRAFT:
      Icon = Dot;
      text = 'Draft';
      className = 'text-caption text-sm';
      break;
    case AB_TEST_STATUSES.DELETED:
      Icon = Dot;
      text = 'Deleted';
      className = 'text-caption text-sm';
      break;
    default:
      Icon = StatusErrorIcon;
      text = 'Error';
      className = 'text-radical-red-500';
      break;
  }

  return (
    <div className={`flex items-center leading-none ${className}`}>
      <div
        className={classNames('mr-2.5 h-2.5 w-2.5 rounded', {
          'bg-california-500': status === AB_TEST_STATUSES.STOPPED,
        })}
      >
        <Icon className="h-2.5 w-2.5 fill-current" aria-label={`a/b test ${text.toLowerCase()}`} />
      </div>{' '}
      {text}
    </div>
  );
}
